"use client";
import React, { useContext, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { device, useBreakpoints } from "../../lib/breakpoints";
import { Col, Row } from "antd";
import { Spacer } from "../../styles/CommonElements";
import { useNavigate, useLocation } from "react-router-dom";
import { trackEvent } from "../../helpers/mixpanel";
import { APP_NAME } from "../../helpers/constants";
import { AuthContext } from "../../context/AuthProvider";
import {
  isLimitedToOpsChatExperience,
  OPS_CHAT_LANDLORD_HOME,
} from "../../helpers/accountProduct";
import IntakeModal from "../../components/IntakeModal";
import { parseHeroAddressParam } from "../../lib/heroAddressParam";
import StaticOpsChatPreview from "../../components/home/StaticOpsChatPreview";

const HomeMain = () => {
  const { isMobile } = useBreakpoints();
  const { user, userInfo } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const addressFromQuery = parseHeroAddressParam(location.search);
  const heroPropertyLabel = addressFromQuery ?? "your portfolio";
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const heroContentRef = useRef(null);
  const [heroContentHeight, setHeroContentHeight] = useState(null);

  useEffect(() => {
    if (isMobile) return;
    const el = heroContentRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { height } = entries[0]?.contentRect ?? {};
      if (typeof height === "number" && height > 0)
        setHeroContentHeight(height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile]);

  useEffect(() => {
    if (location.state?.openFreeIntake) {
      setSelectedPlan("free");
      setShowIntakeModal(true);
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  const handleExplorePlatform = () => {
    trackEvent("Hero Explore Platform Click", {
      section: "hero",
      device_type: isMobile ? "mobile" : "desktop",
    });
    navigate("/#product-features");
  };

  const handleHeroPrimaryCta = () => {
    if (user) {
      trackEvent("Go to Dashboard Click", {
        section: "hero",
        device_type: isMobile ? "mobile" : "desktop",
      });
      navigate(
        isLimitedToOpsChatExperience(userInfo)
          ? OPS_CHAT_LANDLORD_HOME
          : "/dashboard"
      );
      return;
    }
    trackEvent("Hero Free Gateway CTA Click", {
      section: "hero",
      plan: "free",
      device_type: isMobile ? "mobile" : "desktop",
    });
    setSelectedPlan("free");
    setShowIntakeModal(true);
  };

  const handleIntakeSubmit = async (profileData) => {
    try {
      setShowIntakeModal(false);
    } catch (error) {
      console.error("Error processing trial signup:", error);
    }
  };

  const handleCloseModal = () => {
    setShowIntakeModal(false);
    setSelectedPlan("starter");
  };

  return (
    <Container>
      <StyledRow align="middle" justify="center" gutter={[48, 48]} as="section">
        <Col
          xs={24}
          lg={12}
          style={{
            padding: isMobile ? "3rem 1.5rem" : "4rem 2.75rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
          order={1}
        >
          <header
            ref={heroContentRef}
            className="hero-content"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span
              style={{
                display: "block",
                fontSize: "0.72rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                marginBottom: "0.85rem",
                fontWeight: 700,
                color: "rgba(255,255,255,0.78)",
                textAlign: "center",
              }}
            >
              Zero Noise landlording
            </span>
            <Title id="main-heading" className="hero-title">
              {isMobile ? (
                <>
                  Stop the 2 AM
                  <br />
                  tenant texts.
                </>
              ) : (
                "Stop the 2 AM tenant texts."
              )}
            </Title>
            {addressFromQuery ? (
              <>
                <Spacer $marginTop="0.5rem" />
                <HeroPersonalization as="p">
                  Preview for {heroPropertyLabel}
                </HeroPersonalization>
              </>
            ) : null}
            <Spacer $marginTop="0.65rem" />
            <HeroSubtitle className="hero-subtitle">
              Install an AI first-responder for your rentals.
              <br />
              Leo handles tenant questions and filters maintenance.
              <br />
              You stay off-call 24/7.
            </HeroSubtitle>
            <HeroActionStack>
              <ButtonRow
                className="hero-cta"
                style={{
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: "0.85rem",
                }}
              >
                <PrimaryButton
                  onClick={handleHeroPrimaryCta}
                  aria-label={
                    user
                      ? `Go to your ${APP_NAME} dashboard`
                      : "Get started for free for one to three units"
                  }
                  role="button"
                >
                  {user ? (
                    "Go to Dashboard"
                  ) : (
                    <HeroCtaStack>
                      <span>Get Started for Free (1-3 Doors)</span>
                      <CtaSubline>No credit card</CtaSubline>
                    </HeroCtaStack>
                  )}
                </PrimaryButton>
                <ComparePlansButton
                  type="button"
                  onClick={handleExplorePlatform}
                  aria-label="Explore the platform"
                >
                  Explore the platform
                </ComparePlansButton>
              </ButtonRow>
              <HeroTrustPanel role="region" aria-label="Who LandlordOps is for">
                <HeroTrustMain>
                  <HeroTrustKicker>Pick your door count</HeroTrustKicker>
                  <HeroTrustBody>
                    One to three units? Start free with the full LandlordOps OS.
                  </HeroTrustBody>
                  <HeroTrustBody style={{ marginTop: "0.65rem" }}>
                    Growing past that? Upgrade for more doors - same rent, books,
                    vendors, and Leo on every plan.
                  </HeroTrustBody>
                </HeroTrustMain>
                <HeroTrustRule />
                <HeroTrustFoot>
                  No jargon. No 24/7 phone duty. Just a system that works.
                </HeroTrustFoot>
              </HeroTrustPanel>
            </HeroActionStack>
          </header>
        </Col>
        <Col
          xs={24}
          lg={12}
          style={{
            padding: isMobile ? "1rem 1.5rem 3rem 1.5rem" : "3rem 2rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            maxWidth: "100%",
          }}
          order={2}
        >
          <HeroWidgetWrapper
            $height={!isMobile && heroContentHeight ? heroContentHeight : null}
            className="hero-widget-wrapper"
          >
            <PhoneFrame className="hero-widget" data-testid="phone-frame">
              <PhoneScreen>
                <PropertyManagementDemo addressLabel={heroPropertyLabel} />
              </PhoneScreen>
            </PhoneFrame>
          </HeroWidgetWrapper>
        </Col>
      </StyledRow>
      <IntakeModal
        isOpen={showIntakeModal}
        onClose={handleCloseModal}
        onSubmit={handleIntakeSubmit}
        selectedPlan={selectedPlan}
      />
    </Container>
  );
};

// Property Management Demo - shared styling with `/platform` AI First Responder embed
const PropertyManagementDemo = ({ addressLabel }) => (
  <StaticOpsChatPreview
    layout="hero"
    aria-label={`${APP_NAME} OpsChat sample conversation, static visual preview only. The interactive Leo AI demo appears below on this page.`}
    headerSubtitle={`AI assistant for ${addressLabel}`}
    messages={[
      {
        role: "assistant",
        time: "2:31 PM",
        text: (
          <>
            Hi, I&apos;m Leo, your first-responder for {addressLabel}. I answer
            common questions and log maintenance. You only step in when it
            matters.
          </>
        ),
      },
      {
        role: "user",
        time: "2:32 PM",
        text: (
          <>
            The kitchen faucet has been dripping for a few days. Can we get it
            looked at?
          </>
        ),
      },
      {
        role: "assistant",
        time: "2:32 PM",
        text: (
          <>
            Got it. I captured the details. This stays in your thread until you
            or your team schedule it.
          </>
        ),
      },
      {
        role: "user",
        time: "2:33 PM",
        text: <>What if it&apos;s an emergency?</>,
      },
      {
        role: "assistant",
        time: "2:33 PM",
        text: (
          <>
            Burst pipe, no heat, or gas smell? Those go to you right away by
            text and call. You are not left guessing.
          </>
        ),
      },
    ]}
  />
);

export default HomeMain;

// Styled Components
const Container = styled.div`
  min-height: 90vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  padding: 0;
  background: linear-gradient(100deg, #181818 0%, #181818 75%, #2c5f2d 100%);
  overflow: hidden;
  width: 100%;
  max-width: 100vw;
`;

const StyledRow = styled(Row).withConfig({
  shouldForwardProp: (prop) => !["align", "justify", "gutter"].includes(prop),
})`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 3;
  width: 100%;
  display: flex !important;
  flex-wrap: wrap;
  overflow: hidden;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: stretch;
  }
  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const Title = styled.h1`
  font-size: clamp(2.35rem, 6.2vw, 4.25rem);
  font-weight: 800;
  line-height: 1.06;
  margin: 0;
  letter-spacing: -0.04em;
  color: #fff;
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
  text-align: center;
  max-width: 720px;
  overflow-wrap: anywhere;

  .desktop-line-break {
    @media ${device.mobile} {
      display: none;
    }
  }

  @media ${device.mobile} {
    font-size: clamp(2rem, 9vw, 3rem);
    line-height: 1.08;
    max-width: 100%;
  }
`;

const HeroPersonalization = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.88);
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  text-align: center;
  max-width: 580px;
  width: 100%;
  line-height: 1.4;

  @media ${device.mobile} {
    font-size: 0.95rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.02rem;
  line-height: 1.5;
  margin: 0;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  letter-spacing: -0.015em;
  text-align: center;
  max-width: 36rem;
  width: 100%;
  text-wrap: balance;

  @media ${device.mobile} {
    font-size: 0.95rem;
    line-height: 1.52;
    max-width: 100%;
  }
`;

const HeroActionStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.35rem;
  margin-top: 1.35rem;
  width: 100%;
  max-width: 100%;

  @media ${device.mobile} {
    gap: 1.65rem;
    margin-top: 1.5rem;
  }
`;

const HeroTrustPanel = styled.div`
  width: 100%;
  max-width: 40rem;
  padding: 1.15rem 1.2rem 1.2rem;
  background: rgba(0, 0, 0, 0.38);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 14px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 0.95rem;

  @media ${device.mobile} {
    max-width: 100%;
    padding: 1.2rem 1.15rem 1.25rem;
    gap: 1rem;
  }
`;

const HeroTrustMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const HeroTrustKicker = styled.p`
  margin: 0;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  font-family: "Inter", sans-serif;
`;

const HeroTrustBody = styled.p`
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.68;
  color: rgba(255, 255, 255, 0.86);
  font-family: "Inter", sans-serif;
  font-weight: 400;
`;

const HeroTrustRule = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  width: 100%;
  flex-shrink: 0;
`;

const HeroTrustFoot = styled.p`
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.45;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  font-family: "Inter", sans-serif;
  text-align: center;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1.25rem;
  align-items: center;
  justify-content: center;
  margin-top: 0;
  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
    gap: 1.25rem;
    margin-top: 0;
  }
`;

const PrimaryButton = styled.button`
  background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 100%);
  color: #fff;
  border: none;
  padding: 1.2rem 2rem;
  border-radius: 999px;
  font-size: clamp(1rem, 2.8vw, 1.2rem);
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: min(100%, 260px);
  min-height: 52px;
  box-shadow: 0 6px 24px rgba(44, 95, 45, 0.4), 0 2px 8px rgba(44, 95, 45, 0.3);
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  position: relative;
  overflow: hidden;
  letter-spacing: -0.01em;
  &:hover {
    background: linear-gradient(90deg, #4a7c59 0%, #2c5f2d 100%);
    color: #fff;
    transform: scale(1.05);
    box-shadow: 0 10px 40px rgba(44, 95, 45, 0.5),
      0 4px 12px rgba(44, 95, 45, 0.4);
  }
  &:active {
    transform: scale(0.98);
  }
  @media ${device.mobile} {
    min-width: auto;
    width: 100%;
    font-size: 1.1rem;
    padding: 1.2rem 2rem;
  }
`;

const HeroCtaStack = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  text-align: center;
  line-height: 1.2;
`;

const CtaSubline = styled.span`
  font-size: 0.58em;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  opacity: 0.92;
  max-width: 18rem;
`;

const ComparePlansButton = styled.button`
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.55);
  padding: 1.05rem 1.75rem;
  border-radius: 999px;
  font-size: clamp(0.98rem, 2.6vw, 1.12rem);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: min(100%, 240px);
  min-height: 52px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  position: relative;
  overflow: hidden;
  letter-spacing: -0.01em;
  backdrop-filter: blur(10px);
  &:hover {
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.65);
    transform: scale(1.04);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  }
  @media ${device.mobile} {
    min-width: auto;
    width: 100%;
    font-size: 1.05rem;
    padding: 1.1rem 1.5rem;
  }
`;

const HERO_WIDGET_MAX_HEIGHT = 750;

const HeroWidgetWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (min-width: 768px) {
    max-height: ${HERO_WIDGET_MAX_HEIGHT}px;
    ${(props) =>
      typeof props.$height === "number" && props.$height > 0
        ? `
      height: ${props.$height}px;
      display: flex;
      flex-direction: column;
      align-items: center;
      min-height: 0;
    `
        : ""}
  }
`;

const PhoneFrame = styled.div`
  width: 100%;
  max-width: 375px;
  position: relative;
  z-index: 2;
  transform: perspective(1200px) rotateY(-14deg) rotateX(6deg) scale(1.04) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 16px 48px 0 rgba(44, 95, 45, 0.13),
    0 2px 24px 0 rgba(46, 46, 46, 0.08);
  background: linear-gradient(
    120deg,
    rgba(44, 95, 45, 0.08) 0%,
    rgba(255, 255, 255, 0.2) 100%
  );
  border-radius: 36px;
  overflow: visible;
  &:hover {
    transform: perspective(1200px) rotateY(-8deg) rotateX(2deg) scale(1.06) !important;
    box-shadow: 0 32px 64px 0 rgba(44, 95, 45, 0.18),
      0 4px 32px 0 rgba(46, 46, 46, 0.1);
  }
  &::after {
    content: "";
    position: absolute;
    left: 10%;
    bottom: 8%;
    width: 80%;
    height: 30px;
    background: radial-gradient(
      ellipse at center,
      rgba(44, 95, 45, 0.18) 0%,
      transparent 80%
    );
    filter: blur(6px);
    z-index: 0;
    pointer-events: none;
  }
  @media (min-width: 768px) {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    max-height: 100%;
  }
  @media ${device.mobile} {
    transform: none !important;
    max-width: 340px;
    margin: 0 auto;
    box-shadow: 0 8px 24px 0 rgba(44, 95, 45, 0.1),
      0 1px 8px 0 rgba(46, 46, 46, 0.06);
    &:hover {
      transform: none !important;
    }
  }
`;

const PhoneScreen = styled.div`
  background: transparent;
  border-radius: 28px;
  padding: 0.75rem;

  @media ${device.mobile} {
    padding: 0.6rem;
  }
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(44, 95, 45, 0.12),
    inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  position: relative;
  /* Phone notch bar */
  &::before {
    content: "";
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 56px;
    height: 4px;
    background: rgba(255, 255, 255, 0.35);
    border-radius: 2px;
    z-index: 10;
  }
  @media (min-width: 768px) {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;
